/**
 * System Settings Page Component
 * System configuration and settings management
 * Matches the Claude artifact HTML structure
 */

import React, { useState, useEffect } from 'react';
import ManageRegionsModal from '../components/modals/ManageRegionsModal';
import AISettingsCard from '../components/AISettingsCard';
import PulseSettingsCard from '../components/PulseSettingsCard';
import { apiService } from '../services/apiService';
import './SettingsPage.css';

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('General');
  const [allowedRegions, setAllowedRegions] = useState<string[]>(['GB', 'AU', 'IE', 'CA']);
  const [showManageRegionsModal, setShowManageRegionsModal] = useState(false);

  // Load allowed regions on mount
  useEffect(() => {
    loadAllowedRegions();
  }, []);

  const loadAllowedRegions = async () => {
    try {
      const data = await apiService.getAllowedRegions();
      setAllowedRegions(data.allowedRegions);
    } catch (err) {
      console.error('Error loading allowed regions:', err);
      // Keep default regions on error
    }
  };

  const handleSave = () => {
    // TODO: Implement save functionality
    console.log('Settings saved');
  };

  const handleManageRegions = () => {
    setShowManageRegionsModal(true);
  };

  const handleSaveRegions = async (newAllowedRegions: string[]) => {
    try {
      await apiService.setAllowedRegions(newAllowedRegions);
      setAllowedRegions(newAllowedRegions);
      setShowManageRegionsModal(false);
    } catch (err) {
      console.error('Error saving allowed regions:', err);
      throw err; // Re-throw so modal can handle it
    }
  };

  // Get country objects for allowed regions
  const getAllowedCountries = () => {
    const { allCountries } = require('../data/countries');
    return allowedRegions
      .map(code => allCountries.find((c: any) => c.code === code))
      .filter(Boolean);
  };

  const allowedCountries = getAllowedCountries();

  return (
    <div className="settings-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">System Settings</h1>
          <p className="page-sub">Platform-wide configuration</p>
        </div>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'General' ? 'active' : ''}`}
          onClick={() => setActiveTab('General')}
        >
          General
        </button>
        <button 
          className={`tab ${activeTab === 'Betting Limits' ? 'active' : ''}`}
          onClick={() => setActiveTab('Betting Limits')}
        >
          Betting Limits
        </button>
        <button 
          className={`tab ${activeTab === 'Fees' ? 'active' : ''}`}
          onClick={() => setActiveTab('Fees')}
        >
          Fees
        </button>
        <button 
          className={`tab ${activeTab === 'Geo Restrictions' ? 'active' : ''}`}
          onClick={() => setActiveTab('Geo Restrictions')}
        >
          Geo Restrictions
        </button>
        <button
          className={`tab ${activeTab === 'App Versions' ? 'active' : ''}`}
          onClick={() => setActiveTab('App Versions')}
        >
          App Versions
        </button>
        <button
          className={`tab ${activeTab === 'Versus AI' ? 'active' : ''}`}
          onClick={() => setActiveTab('Versus AI')}
        >
          Versus AI
        </button>
        <button
          className={`tab ${activeTab === 'Pulse' ? 'active' : ''}`}
          onClick={() => setActiveTab('Pulse')}
        >
          Pulse
        </button>
      </div>

      <div className="grid-2">
        {activeTab === 'Versus AI' && <AISettingsCard />}
        {activeTab === 'Pulse' && <PulseSettingsCard />}
        {activeTab !== 'Versus AI' && activeTab !== 'Pulse' && <>
        {/* Default Betting Limits Card */}
        <div className="card">
          <div className="card-head">
            <span className="card-title">💰 Default Betting Limits</span>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">Minimum Bet</label>
              <input type="text" className="form-input" defaultValue="£5" />
            </div>
            <div className="form-group">
              <label className="form-label">Maximum Bet</label>
              <input type="text" className="form-input" defaultValue="£10,000" />
            </div>
            <div className="form-group">
              <label className="form-label">New User Daily Deposit Limit</label>
              <input type="text" className="form-input" defaultValue="£500" />
            </div>
            <div className="form-group">
              <label className="form-label">New User Weekly Loss Limit</label>
              <input type="text" className="form-input" defaultValue="£1,000" />
            </div>
            <button className="btn btn-primary" onClick={handleSave}>
              Save Changes
            </button>
          </div>
        </div>

        {/* Fees & Commissions Card */}
        <div className="card">
          <div className="card-head">
            <span className="card-title">💸 Fees & Commissions</span>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">Platform Fee (on winning bets)</label>
              <input type="text" className="form-input" defaultValue="9.5%" />
            </div>
            <div className="form-group">
              <label className="form-label">Referral Commission (Share & Earn)</label>
              <input type="text" className="form-input" defaultValue="5%" />
            </div>
            <div className="form-group">
              <label className="form-label">Withdrawal Fee</label>
              <input type="text" className="form-input" defaultValue="£0 (Free)" />
            </div>
            <button className="btn btn-primary" onClick={handleSave}>
              Save Changes
            </button>
          </div>
        </div>

        {/* Geo Restrictions Card */}
        <div className="card">
          <div className="card-head">
            <span className="card-title">🌍 Geo Restrictions</span>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">Allowed Regions</label>
              <div className="region-tags">
                {allowedCountries.length > 0 ? (
                  allowedCountries.map((country: any) => (
                    <span key={country.code} className="status green">
                      {country.flag} {country.name}
                    </span>
                  ))
                ) : (
                  <div className="empty-regions">No regions allowed</div>
                )}
              </div>
            </div>
            <button className="btn btn-secondary" onClick={handleManageRegions}>
              Manage Regions
            </button>
          </div>
        </div>

        {/* App Versions Card */}
        <div className="card">
          <div className="card-head">
            <span className="card-title">📱 App Versions</span>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">iOS Current Version</label>
              <input type="text" className="form-input" defaultValue="1.2.0" />
            </div>
            <div className="form-group">
              <label className="form-label">iOS Minimum Version</label>
              <input type="text" className="form-input" defaultValue="1.0.0" />
            </div>
            <div className="form-group">
              <label className="form-label">Android Current Version</label>
              <input type="text" className="form-input" defaultValue="1.2.0" />
            </div>
            <button className="btn btn-primary" onClick={handleSave}>
              Update
            </button>
          </div>
        </div>
        </>}
      </div>

      <ManageRegionsModal
        isOpen={showManageRegionsModal}
        allowedRegions={allowedRegions}
        onClose={() => setShowManageRegionsModal(false)}
        onSave={handleSaveRegions}
      />
    </div>
  );
};

export default SettingsPage;
